<script>
    import { onMount, onDestroy } from "svelte";

    export let isFocused;
    export let scrollTop = 0;
    export let isBorderless = false;

    let container;
    let bar;
    let content;
    let size;
    let knob;
    let isDragging = false;
    let initialDragLocation;
    let initialContentOffset;

    function didScrollContent() {
        const scrollMax = content.scrollHeight - container.clientHeight;
        const knobMax = bar.clientHeight - knob.clientHeight - 4.0;

        const top = Math.min(
            Math.max((content.scrollTop / scrollMax) * knobMax, 0.0),
            knobMax
        );

        knob.style.top = `${top}px`;
        scrollTop = content.scrollTop;
    }

    function resize() {
        if (!container || !content) {
            return;
        }

        const knobHeight = Math.round(
            (container.clientHeight / content.scrollHeight) * bar.clientHeight
        );

        knob.hidden = knobHeight > bar.clientHeight;
        knob.style.height = `${knobHeight}px`;

        didScrollContent();
    }

    if (typeof ResizeObserver !== "undefined") {
        const observer = new ResizeObserver(resize);

        onMount(() => {
            observer.observe(container);
            observer.observe(size);
        });

        onDestroy(() => {
            observer.unobserve(container);
            observer.unobserve(size);
        });
    }

    function didMouseDown(e) {
        e.preventDefault();
        e.stopPropagation();

        isDragging = true;
        initialDragLocation =
            e.touches && e.touches.length ? e.touches[0].clientY : e.clientY;
        initialContentOffset = content.scrollTop;
    }

    function didMouseMove(e) {
        if (!isDragging) {
            return;
        }

        e.preventDefault();
        e.stopPropagation();

        const y =
            e.touches && e.touches.length ? e.touches[0].clientY : e.clientY;

        const offset =
            (y - initialDragLocation) * (bar.clientHeight / knob.clientHeight);

        content.scrollTop = initialContentOffset + offset;
    }

    function didMouseUp() {
        isDragging = false;
    }
</script>

<svelte:window
    on:mousemove={didMouseMove}
    on:touchmove={didMouseMove}
    on:mouseup={didMouseUp}
    on:touchend={didMouseUp}
/>

<div
    class="scroll"
    class:focused={isFocused}
    class:dragging={isDragging}
    class:borderless={isBorderless}
    bind:this={container}
>
    <div class="content" bind:this={content} on:scroll={didScrollContent}>
        <div bind:this={size}>
            <slot />
        </div>
    </div>
    <div class="bar" bind:this={bar}>
        <div
            class="knob"
            bind:this={knob}
            on:mousedown={didMouseDown}
            on:touchstart={didMouseDown}
        >
            <div class="grip">
                <hr />
                <hr />
                <hr />
                <hr />
            </div>
        </div>
    </div>
</div>

<style>
    .scroll {
        position: relative;
        box-sizing: border-box;
        background: white;
        padding-right: 16px;
        width: 100%;
        height: 100%;
    }

    .content {
        box-sizing: border-box;
        padding: calc(var(--chrome-depth) * 3);
        width: 100%;
        max-height: 100%;
        overflow: auto;
        overflow-x: hidden;
        scrollbar-width: none;
    }

    .borderless .content {
        padding: 0;
    }

    :global(
            .focused .content p,
            .focused .content h3,
            .focused .content td,
            .focused .selectable
        ) {
        user-select: text;
        -webkit-user-select: text;
    }

    :global(
            .resizing .content p,
            .resizing .content h3,
            .resizing .content td,
            .resizing .selectable
        ) {
        user-select: none;
        -webkit-user-select: none;
    }

    :global(
            .dragging .content p,
            .dragging .content h3,
            .dragging .selectable
        ) {
        user-select: none;
        -webkit-user-select: none;
    }

    .content::-webkit-scrollbar {
        display: none;
    }

    .bar {
        position: absolute;
        top: 0;
        right: 0;
        filter: saturate(0%);
        border-bottom: #eeeeee 16px solid;
        background: #eeeeee;
        width: 16px;
        height: calc(100% - 16px);
    }

    @media only screen and (max-width: 734px) {
        .bar {
            border-bottom: none;
            height: 100%;
        }
    }

    .focused .bar {
        filter: none;
    }

    .knob {
        position: absolute;
        box-sizing: border-box;

        border-top: var(--knob-border-color-light) var(--chrome-depth) outset;
        border-right: var(--knob-border-color-dark) var(--chrome-depth) outset;
        border-bottom: var(--knob-border-color-dark) var(--chrome-depth) outset;
        border-left: var(--knob-border-color-light) var(--chrome-depth) outset;
        background: var(--knob-hsl);
        width: 100%;
    }

    .knob:active {
        filter: brightness(0.8);
    }

    .grip {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translateX(-50%) translateY(-50%);
    }

    hr {
        opacity: 0.5;
        margin-top: 1px;
        margin-bottom: 1px;
        width: 5px;
    }
</style>
